import {defineStore} from 'pinia';
import axios from 'axios';
import {globals} from 'hit-online-web-ui/src/globals';
import {ActionService, DataService} from '../../api';
import {v4 as uuidv4} from 'uuid';
import {HitUUIDUtils} from '@hit/components';
import {BookkeepingForwardConfig} from './specificConfigs';

function parseLocale(locale, format = 'PascalCase') {
  if (!locale || typeof locale !== 'string') {
    locale = 'fr-BE';
  }
  const localeBase = locale.replaceAll('_', '').replaceAll('-', '');
  if (localeBase.length !== 4) {
    throw new Error('Impossible to parse locale. Invalid number of characters');
  }
  if (format === 'PascalCase') {
    return (
      localeBase[0].toUpperCase() +
      localeBase[1].toLowerCase() +
      localeBase[2].toUpperCase() +
      localeBase[3].toLowerCase()
    );
  } else if (format === 'snake_case') {
    return (
      localeBase[0].toLowerCase() +
      localeBase[1].toLowerCase() +
      '_' +
      localeBase[2].toLowerCase() +
      localeBase[3].toLowerCase()
    );
  } else if (format === 'camelCase') {
    return (
      localeBase[0].toLowerCase() +
      localeBase[1].toLowerCase() +
      localeBase[2].toUpperCase() +
      localeBase[3].toLowerCase()
    );
  } else if (format === 'id') {
    return (
      localeBase[0].toLowerCase() +
      localeBase[1].toLowerCase() +
      '-' +
      localeBase[2].toUpperCase() +
      localeBase[3].toUpperCase()
    );
  } else {
    throw new Error(
      'Locale parser does only support the following formats: PascalCase, snake_case and camelCase',
    );
  }
}

function parseDesignation(locale, format = 'short') {
  const localePascalCase = parseLocale(locale, 'PascalCase');
  switch (localePascalCase) {
    case 'EnGb':
      return format === 'short' ? 'En-GB' : 'English (United Kingdom)';
    case 'FrBe':
      return format === 'short' ? 'Fr-BE' : 'Français (Belgique)';
    case 'NlBe':
      return format === 'short' ? 'Nl-BE' : 'Nederlands (België)';
    case 'DeBe':
      return format === 'short' ? 'De-BE' : 'Deutsch (Belgien)';
    case 'MrIn':
      return format === 'short' ? 'Mr-IN' : 'Marathi (India)';
  }
}

function getDefaultValue(configLabel) {
  if (configLabel.includes('collapsed')) {
    return false;
  } else if (configLabel.includes('columns')) {
    return [];
  } else if (configLabel.includes('pagination')) {
    return 20;
  } else if (configLabel === 'action.list.selected-tab') {
    return 'list';
  } else if (configLabel === 'form.list.selected-tab') {
    return 'ongoing-forms';
  } else if (configLabel === 'transaction.list.selected-tab') {
    return 'all-transactions';
  } else if (configLabel === 'transaction.summary.selected-tab') {
    return 'tax';
  } else if (configLabel === 'address.project-part-panel.selected-tab') {
    return 'work-parts';
  } else if (configLabel === 'project.list.selected-tab') {
    return 'projects';
  } else if (configLabel === 'purchase.list.selected-tab') {
    return 'purchase_order';
  } else if (configLabel.endsWith('.purchase-panel.selected-tab')) {
    return 'price-requests';
  } else if (
    configLabel.startsWith('qualification.') &&
    configLabel.endsWith('.default')
  ) {
    return [];
  } else if (
    configLabel === 'user.planning.prioritise-projects-involving-staff'
  ) {
    return false;
  } else if (configLabel === 'metrics.formula-mode') {
    return 'EXPRESSION';
  } else {
    return {};
  }
}

function getConfigDatatype(configLabel) {
  if (configLabel.includes('collapsed')) {
    return 'BOOLEAN';
  } else if (configLabel.includes('columns')) {
    return 'JSON';
  } else if (configLabel.includes('pagination')) {
    return 'INT';
  } else if (configLabel === 'action.rls-restriction') {
    return 'VARCHAR';
  } else if (configLabel === 'nsso.error-notification.user-id') {
    return 'JSON';
  } else if (configLabel === 'transaction.user-rls-access') {
    return 'JSON';
  } else if (configLabel === 'transaction.user-rls-enabled') {
    return 'BOOLEAN';
  } else if (configLabel === 'notification.send-by-mail') {
    return 'BOOLEAN';
  } else if (
    configLabel === 'user.planning.prioritise-projects-involving-staff'
  ) {
    return 'BOOLEAN';
  } else if (
    configLabel === 'bookkeeping.responsible-purchase.mandatory' ||
    configLabel === 'bookkeeping.responsible-sale.mandatory'
  ) {
    return 'BOOLEAN';
  } else if (configLabel === 'metrics.formula-mode') {
    return 'VARCHAR';
  } else {
    return 'JSON';
  }
}

/**
 * When a table uses configSuffix (tabs), new keys are e.g. project.list.sorting.projects.
 * Legacy rows still use project.list.sorting — read fallback so existing users keep their sort.
 */
function isEmptyTableSorting(sorting) {
  return (
    !sorting ||
    typeof sorting !== 'object' ||
    !sorting.property ||
    !sorting.order
  );
}

function isEmptyTableFilters(filters) {
  if (filters == null) {
    return true;
  }
  if (Array.isArray(filters)) {
    return filters.length === 0;
  }
  if (typeof filters === 'object') {
    return Object.keys(filters).length === 0;
  }
  return true;
}

export const useConfigurationStore = defineStore('configuration', {
  state: () => ({
    configurations: {},
    tablePageConfigurations: {},
    customButtons: {},
    actionOverrides: {},
    customPanels: {},
    customReports: [],
    tableFilters: {},
    tableSorting: {},
    favoriteScreens: [],
    hiddenPanels: {},
    bookkeepingForwardingConfig: undefined,
    lostConnectionTime: null,
    qualifications: [],
  }),

  getters: {
    lostServerConnection: (state) => state.lostConnectionTime !== null,
    sidebarSide: (state) =>
      state.configurations['user.edit-panel.side'] ?? 'right',
    nextServerConnectionAttempt: (state) =>
      state.lostConnectionTime
        ? new Date(state.lostConnectionTime.getTime() + 15000)
        : null,
    configuration: (state) => state.configurations,
    favorites: (state) => {
      return Array.isArray(state?.favoriteScreens) ? state.favoriteScreens : [];
    },
    positionsActivatedStaffHours: (state) =>
      state.configurations?.['company.staff-hours-on-positions.enabled'] ??
      false,
    generateLedgerEntriesConfig: (state) =>
      state.configurations?.['bookkeeping.generate-ledger-entries'] === true,
    useQuotationValidityDays: (state) =>
      (state.configurations?.['project-part.quotation.validity-days'] ?? 0) > 0,
    userLanguage: (state) => {
      /**
       * Returns the UI language of the user in the original format
       * => en-GB
       */
      return state.configurations['user.locale'] || 'fr-BE';
    },
    userLanguageSnakeCase: (state) => {
      /**
       * Returns the selected UI language in snake_case format
       * => en_gb
       */
      return parseLocale(
        state.configurations['user.locale'] || 'fr-BE',
        'snake_case',
      );
    },
    userLanguagePascalCase: (state) => {
      /**
       * Returns the selected UI language in PascalCase format
       * => EnGb
       */
      return parseLocale(
        state.configurations['user.locale'] || 'fr-BE',
        'PascalCase',
      );
    },
    mainLanguageSnakeCase: (state) => {
      /**
       * Returns the first language of the company in snake_case format
       * company.locales = ['en_gb', 'fr_be']
       * => en_gb
       */
      const locales = state.configurations['company.locales'];
      return parseLocale(
        Array.isArray(locales) && locales[0] ? locales[0] : 'fr_be',
        'snake_case',
      );
    },
    mainLanguagePascalCase: (state) => {
      /**
       * Returns the first language of the company in PascalCase format
       * company.locales = ['en_gb', 'fr_be']
       * => EnGb
       */
      const locales = state.configurations['company.locales'];
      return parseLocale(
        Array.isArray(locales) && locales[0] ? locales[0] : 'fr_be',
        'PascalCase',
      );
    },
    companyLanguagesSnakeCase: (state) => {
      /**
       * Returns all the company locales in the snake_case format = original format from the database
       * company.locales = ['en_gb', 'fr_be']
       * => ['en_gb', 'fr_be']
       */
      const locales = state.configurations['company.locales'];
      return Array.isArray(locales) && locales.length ? locales : ['fr_be'];
    },
    companyLanguagesPascalCase: (state) => {
      /**
       * Returns all the company locales in the PascalCase format
       * company.locales = ['en_gb', 'fr_be']
       * => ['EnGb', 'FrBe']
       */
      const locales = state.configurations['company.locales'];
      const list =
        Array.isArray(locales) && locales.length ? locales : ['fr_be'];
      return list.map((locale) => parseLocale(locale, 'PascalCase'));
    },
    companyLanguagesObject: (state) => {
      const companyLanguages = [];
      const locales = state.configurations['company.locales'];
      const list =
        Array.isArray(locales) && locales.length ? locales : ['fr_be'];
      list.forEach((language) => {
        companyLanguages.push({
          id: parseLocale(language, 'id'),
          valueSuffix: parseLocale(language, 'PascalCase'),
          hitI18nCode: parseLocale(language, 'snake_case'),
          longDesignation: parseDesignation(language, 'long'),
          shortDesignation: parseDesignation(language, 'short'),
        });
      });
      return companyLanguages;
    },
  },

  actions: {
    async initConfigurations() {
      /**
       * Used to store all the configs fetched during application startup in the store
       * The qualifications can only be loaded once the locale is initialized via the global configs
       */
      const configBatch = [
        this.initCompanyConfigs(),
        this.initCustomConfigs(),
        this.initGlobalConfigs(),
      ];
      await Promise.all(configBatch);
      this.initQualifications();
    },

    /**
     * We store the defined custom buttons in the store to avoid one request each time a view is loaded
     * The custom buttons are universal for all the users and not user specific
     */
    async initCompanyConfigs() {
      try {
        const res = await DataService.read('config', {
          attributes: 'label,name,value',
          filters: {
            or: '(label.eq.custom.button,label.eq.custom.action.override,label.eq.custom.panel,label.eq.custom.report,label.ilike.*no.type)',
          },
        });
        if (res && res.data) {
          this.customReports = [];
          this.customPanels = {};
          res.data.forEach((config) => {
            if (config['label'] === 'custom.button') {
              this.customButtons[config['name'].toLowerCase()] = config.value;
            } else if (config['label'] === 'custom.action.override') {
              this.actionOverrides[config['name'].toLowerCase()] = config.value;
            } else if (config['label'] === 'custom.panel') {
              this.customPanels[config['name'].toLowerCase()] = config.value;
            } else if (config['label'] === 'custom.report') {
              this.customReports.push(config.value);
            } else if (config['label'].includes('no.type')) {
              this.configurations[config['label']] = config.value;
            }
          });
        }
      } catch (error) {
        console.error('Failed to load company configs', error);
      }
    },

    /**
     * We store the user specific table filters in the store to avoid making one
     * HTTP request to config each time a table is loaded
     */
    async initCustomConfigs() {
      const labelsToFetch = [
        'label.ilike.*filters*',
        'label.ilike.*sorting*',
        'label.eq.user.favorites',
        'label.eq.user.panels.hidden',
        'label.eq.user.edit-panel.side',
        'label.ilike.*list.selected-tab',
        'label.ilike.*columns.widths',
        'label.eq.user.planning.prioritise-projects-involving-staff',
      ];
      try {
        const res = await DataService.read('config', {
          attributes: 'label,value,name',
          filters: {
            or: `(${labelsToFetch.join(',')})`,
            user_id: `eq.${globals.$appContext.user.id}`,
          },
        });
        if (res && res.data) {
          res.data.forEach((filter) => {
            if (filter['label'] === 'user.favorites') {
              // By error, there can be a NULL value stored in the database. In this case, we delete the obsolete config
              if (!filter['value']) {
                this.favoriteScreens = [];
                DataService.delete('config', {
                  label: 'eq.user.favorites',
                  user_id: `eq.${globals.$appContext.user.id}`,
                });
              } else {
                this.favoriteScreens = filter['value'];
              }
            } else if (filter['label'] === 'user.panels.hidden') {
              this.hiddenPanels[filter['name']] = filter['value'];
            } else if (
              filter['label'] ===
              'user.planning.prioritise-projects-involving-staff'
            ) {
              this.configurations[filter['label']] =
                filter['value'] === true || filter['value'] === 'true';
            } else {
              const configType = filter['label'].split('.')[2];
              if (configType === 'filters') {
                this.tableFilters[filter['label']] = filter['value'];
              } else if (configType === 'sorting') {
                this.tableSorting[filter['label']] = filter['value'];
              }
            }
          });
        }
      } catch (error) {
        console.error('Failed to load custom configs', error);
      }
    },

    async initGlobalConfigs() {
      return ActionService.get('config/global').then((res) => {
        if (!res || res.data == null || res.data === '') {
          console.error('Failed to load global configs: empty response');
          return;
        }
        let payload = res.data;
        if (typeof payload === 'string') {
          const trimmed = payload.trim();
          if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
            console.error(
              'Failed to load global configs: non-JSON response',
              trimmed.slice(0, 120),
            );
            return;
          }
          try {
            payload = JSON.parse(trimmed);
          } catch (error) {
            console.error('Failed to parse global configs JSON', error);
            return;
          }
        }
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
          console.error('Failed to load global configs: invalid payload');
          return;
        }
        const bok_config = new BookkeepingForwardConfig();
        Object.entries(payload).forEach(([key, value]) => {
          if (key.includes('bookkeeping.forward')) {
            bok_config.processInitConfig(key, value);
          } else {
            this.configurations[key] = value;
          }
        });
        this.bookkeepingForwardingConfig = bok_config;
      });
    },

    /** Loads all the available qualifications from the database to not always make a request to display the column */
    initQualifications() {
      DataService.read('qualification', {
        attributes: `id,designation${this.userLanguagePascalCase}:designation_${this.userLanguageSnakeCase},designation${this.mainLanguagePascalCase}:designation_${this.mainLanguageSnakeCase}`,
      }).then((res) => {
        this.qualifications = res.data.map((qual) => {
          return {
            id: qual.id,
            designation:
              qual[`designation${this.userLanguagePascalCase}`] ||
              qual[`designation${this.mainLanguagePascalCase}`],
          };
        });
      });
    },

    getConfiguration(configLabel, customDefaultValue = undefined) {
      /**
       * Used to get the actual value of a config with fallback to default value -> parameter or application default
       */
      if (configLabel in this.configurations) {
        return this.configurations[configLabel];
      }
      return customDefaultValue !== undefined
        ? customDefaultValue
        : getDefaultValue(configLabel);
    },

    /**
     * Returns the list of custom buttons that have been stored during loading process
     */
    getCustomButtons(view) {
      const viewName = view.toLowerCase();
      return this.customButtons?.[viewName] ?? [];
    },

    /**
     * Returns action button overrides for a view (custom.action.override config).
     */
    getActionOverrides(view) {
      const viewName = view.toLowerCase();
      return this.actionOverrides?.[viewName] ?? [];
    },

    /**
     * Returns the list of custom panels that have been stored during loading process
     */
    getCustomPanels(view) {
      const viewName = view.toLowerCase();
      return this.customPanels?.[viewName] ?? [];
    },

    getCustomReports(templateType) {
      const type = (templateType ?? '').toLowerCase();
      const entries = Array.isArray(this.customReports)
        ? this.customReports
        : Object.values(this.customReports || {});

      const getModules = (entry) =>
        Array.isArray(entry?.report_available_modules)
          ? entry.report_available_modules.map((module) =>
              String(module).toLowerCase(),
            )
          : [];

      const extractReports = (entry) => {
        if (!entry || typeof entry !== 'object') {
          return [];
        }
        if (Array.isArray(entry)) {
          return entry;
        }
        if (entry.id || entry.templateFileId) {
          return [entry];
        }
        return Object.entries(entry)
          .filter(
            ([key, value]) =>
              key !== 'report_available_modules' &&
              value &&
              typeof value === 'object' &&
              !Array.isArray(value),
          )
          .map(([, value]) => value);
      };

      return entries
        .filter((entry) => getModules(entry).includes(type))
        .flatMap(extractReports);
    },

    getCustomReportDefinition(templateType, selectedTemplateId) {
      if (!selectedTemplateId) {
        return null;
      }
      const reportId = String(selectedTemplateId);
      const reports = this.getCustomReports(templateType);
      const reportById =
        reports.find((r) => String(r?.id ?? '') === reportId) ?? null;
      if (reportById) {
        return reportById;
      }
      // Backward compatibility: match templateFileId only if id is missing.
      return (
        reports.find((r) => {
          const id = String(r?.id ?? '').trim();
          const templateFileId = String(r?.templateFileId ?? '');
          return !id && templateFileId === reportId;
        }) ?? null
      );
    },

    getLatestPrintTemplate(templateType) {
      return DataService.read('config', {
        attributes: 'label,name,value',
        filters: {
          label: 'eq.report.config.latest-print-template',
          name: `eq.${templateType}`,
          user_id: `eq.${globals.$appContext.user.id}`,
        },
      }).then((res) => {
        if (res && res.data && res.data.length > 0) {
          return res.data[0].value;
        }
        return null;
      });
    },

    setLatestPrintTemplate(templateType, selectedTemplateId) {
      return DataService.delete('config', {
        label: 'eq.report.config.latest-print-template',
        name: `eq.${templateType}`,
        user_id: `eq.${globals.$appContext.user.id}`,
      }).then(() => {
        if (!selectedTemplateId) {
          return null;
        }
        return DataService.create('config', {
          id: HitUUIDUtils.generate(),
          label: 'report.config.latest-print-template',
          user_id: globals.$appContext.user.id,
          name: templateType,
          visible: false,
          customisable: false,
          datatype: 'JSON',
          value: selectedTemplateId,
          global: false,
        });
      });
    },

    /**
     * Returns the dictionary with the stored filters for the given table
     */
    getTableFilters(table, configSuffix = null) {
      // For browser modals, we do not apply any saved filter
      if (!table || table.includes('browser.')) return [];
      const baseKey = `${table}.filters`;
      if (configSuffix) {
        const suffixedKey = `${baseKey}.${configSuffix}`;
        const fromSuffix = this.tableFilters?.[suffixedKey];
        if (!isEmptyTableFilters(fromSuffix)) {
          return fromSuffix;
        }
        return this.tableFilters?.[baseKey] ?? [];
      }
      return this.tableFilters?.[baseKey] ?? [];
    },

    /**
     * Returns the dictionary that stores the sorting value for the given table
     */
    getTableSorting(table, configSuffix = null) {
      const baseKey = `${table}.sorting`;
      if (configSuffix) {
        const suffixedKey = `${baseKey}.${configSuffix}`;
        const fromSuffix = this.tableSorting?.[suffixedKey];
        if (!isEmptyTableSorting(fromSuffix)) {
          return fromSuffix;
        }
        return this.tableSorting?.[baseKey] ?? {};
      }
      return this.tableSorting?.[baseKey] ?? {};
    },

    /**
     * Sets the table filters in the store only (no DB persistence).
     * Use for initial filters from route etc. so that "clear filters" still works.
     */
    setTableFiltersLocal(table, filters, configSuffix = null) {
      let filterName = `${table}.filters`;
      if (configSuffix) {
        filterName = `${filterName}.${configSuffix}`;
      }
      if (Object.keys(filters).length === 0) {
        delete this.tableFilters[filterName];
      } else {
        this.tableFilters[filterName] = filters;
      }
    },

    /**
     * Stores the new value of the filters in the store and in the database
     */
    updateTableFilters(table, filters, configSuffix = null) {
      const saveInDb = table && !table.includes('browser.');
      let filterName = `${table}.filters`;
      if (configSuffix) {
        filterName = `${filterName}.${configSuffix}`;
      }
      const dbFilter = {
        label: `eq.${filterName}`,
        user_id: `eq.${globals.$appContext.user.id}`,
      };
      if (Object.keys(filters).length === 0) {
        if (saveInDb) {
          DataService.delete('config', dbFilter);
        }
        delete this.tableFilters[filterName];
      } else {
        if (saveInDb) {
          const existingConfig = this.tableFilters[filterName];
          if (existingConfig) {
            DataService.update(
              'config',
              {
                label: `eq.${filterName}`,
                user_id: `eq.${globals.$appContext.user.id}`,
              },
              {
                value: filters,
              },
            );
          } else {
            DataService.create('config', {
              id: HitUUIDUtils.generate(),
              label: filterName,
              user_id: globals.$appContext.user.id,
              visible: false,
              customisable: true,
              datatype: 'JSON',
              value: filters,
              global: false,
            });
          }
        }
        this.tableFilters[filterName] = filters;
      }
    },

    /**
     * Stores the new value of the sort property and order in the database
     */
    updateTableSorting(table, sorting, configSuffix = null) {
      let sortingName = `${table}.sorting`;
      if (configSuffix) {
        sortingName = `${sortingName}.${configSuffix}`;
      }
      this.tableSorting[sortingName] = sorting;
      DataService.delete('config', {label: `eq.${sortingName}`}).then(() => {
        if (sorting?.property && sorting?.order) {
          DataService.create('config', {
            id: HitUUIDUtils.generate(),
            label: sortingName,
            user_id: globals.$appContext.user.id,
            visible: false,
            customisable: true,
            datatype: 'JSON',
            value: sorting,
            global: false,
          });
        }
      });
    },

    /** Updates / Deletes the hidden configuration depending on the new value */
    updateHiddenConfiguration(
      configLabel,
      newValue,
      forceIsDefault = false,
      forceDatatype = null,
    ) {
      const datatype = forceDatatype ?? getConfigDatatype(configLabel);
      if (newValue === getDefaultValue(configLabel) || forceIsDefault) {
        delete this.configurations[configLabel];
        return DataService.delete('config', {
          label: `eq.${configLabel}`,
          user_id: `eq.${globals.$keycloak.tokenParsed.sub}`,
        });
      } else if (configLabel in this.configurations) {
        this.configurations[configLabel] = newValue;
        return DataService.update(
          'config',
          {
            label: `eq.${configLabel}`,
            user_id: `eq.${globals.$keycloak.tokenParsed.sub}`,
          },
          {value: newValue},
          {
            headers: {
              Prefer: 'return=representation',
            },
          },
        ).then((res) => {
          if (res.status === 200 && res.data && res.data.length > 0) {
            return res;
          }

          return DataService.create('config', {
            id: uuidv4(),
            label: configLabel,
            user_id: globals.$keycloak.tokenParsed.sub,
            visible: false,
            customisable: true,
            datatype: datatype,
            value: newValue,
            global: false,
          });
        });
      } else {
        this.configurations[configLabel] = newValue;
        return DataService.create('config', {
          id: uuidv4(),
          label: configLabel,
          user_id: globals.$keycloak.tokenParsed.sub,
          visible: false,
          customisable: true,
          datatype: datatype,
          value: newValue,
          global: true,
        });
      }
    },

    async updateVisibleConfiguration(configLabel, newValue) {
      /**
       * Updates the config value for configs that have the visible flag set to true (locales, etc)
       */
      if (configLabel in this.configurations) {
        this.configurations[configLabel] = newValue;
      }
      return axios
        .post(
          globals.$appContext.actionUrl +
            `config/single?config_label=${configLabel}`,
          JSON.stringify({new_value: newValue}),
          {
            headers: {
              Authorization: 'Bearer ' + globals.$keycloak.token,
              'Content-Profile': globals.$keycloak.tokenParsed.schema_id,
            },
          },
        )
        .catch(() => {
          console.error(
            'An error happened during the update of the following config : ',
            configLabel,
          );
        });
    },
    async deleteConfiguration(configLabel) {
      const auth = {
        Authorization: 'Bearer ' + globals.$keycloak.token,
        'Content-Profile': globals.$keycloak.tokenParsed.schema_id,
      };
      await axios.delete(
        `${globals.$appContext.actionUrl}config/single?config_label=${configLabel}`,
        {headers: auth},
      );
      const defaultValue = await axios.get(
        `${globals.$appContext.actionUrl}config/single?config_label=${configLabel}`,
        {headers: auth},
      );
      if (defaultValue.status === 200) {
        this.configurations[configLabel] = defaultValue.data.value;
      }
    },

    /**
     * Sets the value as company default
     */
    async setAsDefault(configLabel, value, forceUpdate = false) {
      if (configLabel in this.configurations || forceUpdate) {
        const filters = {
          label: `eq.${configLabel}`,
          user_id: 'is.null',
        };
        return DataService.update('config', filters, {
          value: value,
        });
      } else {
        return DataService.create('config', {
          id: HitUUIDUtils.generate(),
          label: configLabel,
          visible: true,
          datatype: getConfigDatatype(configLabel),
          value: value,
          global: true,
        });
      }
    },

    /**
     * Returns the flag to check if the sync from Hit-Office is activated for this entity
     */
    hasSyncActivated(entityName) {
      return this.getConfiguration(`sync.${entityName}.active`, false);
    },

    getTablePage(tableName) {
      return this.tablePageConfigurations[tableName] ?? 0;
    },

    setTablePage(tableName, selectedPage) {
      this.tablePageConfigurations[tableName] = selectedPage;
    },

    updateFavoritesList(newFavoritesList) {
      this.favoriteScreens = newFavoritesList;
    },

    getHiddenPanels(viewName) {
      return this.hiddenPanels[viewName] ?? [];
    },

    async updateHiddenPanels(viewName, newHiddenPanels) {
      if (newHiddenPanels.length > 0) {
        if (this.hiddenPanels[viewName]) {
          this.hiddenPanels[viewName] = newHiddenPanels;
          return DataService.update(
            'config',
            {
              label: 'eq.user.panels.hidden',
              name: `eq.${viewName}`,
              user_id: `eq.${globals.$appContext.user.id}`,
            },
            {
              value: newHiddenPanels,
            },
          );
        } else {
          this.hiddenPanels[viewName] = newHiddenPanels;
          return DataService.create('config', {
            id: HitUUIDUtils.generate(),
            label: 'user.panels.hidden',
            user_id: globals.$appContext.user.id,
            name: viewName,
            visible: false,
            customisable: true,
            datatype: 'JSON',
            value: newHiddenPanels,
            global: false,
          });
        }
      } else {
        delete this.hiddenPanels[viewName];
        return DataService.delete('config', {
          label: 'eq.user.panels.hidden',
          name: `eq.${viewName}`,
          user_id: `eq.${globals.$appContext.user.id}`,
        });
      }
    },

    /**
     * The function needs to handle both cases (historical list and new dictionary)
     */
    getColumnsConfigs(viewName) {
      let hidden = undefined;
      let order = undefined;
      let qualifications = [];
      const config = this.configurations[`${viewName}.columns`];
      if (!config) {
        hidden = null;
      } else if (Array.isArray(config)) {
        // Code when the hidden columns where stored as a list
        hidden = config;
      } else {
        // Code when the column config is stored as a dictionary
        hidden = config?.hidden ?? null;
        order = config?.order ?? undefined;
        qualifications = config?.qualifications ?? [];
      }
      return {hidden, order, qualifications};
    },

    updateColumnsConfig(viewName, hidden, order, qualifications = undefined) {
      const hiddenToUse = hidden ? hidden : ['$ShowAllColumns$'];
      const configObject = {hidden: hiddenToUse};
      if (order) configObject.order = order;

      // If the qualifications are not provided, we keep the existing ones
      let qualificationsUpdate = qualifications;
      if (qualifications === undefined) {
        const {qualifications} = this.getColumnsConfigs(viewName);
        qualificationsUpdate = qualifications;
      }
      if (qualificationsUpdate.length > 0) {
        qualificationsUpdate = qualificationsUpdate.filter((qualId) =>
          this.qualifications.some((qual) => qual.id === qualId),
        );
        configObject.qualifications = qualificationsUpdate;
      }

      const isDefault =
        hiddenToUse.length === 1 &&
        hiddenToUse[0] === '$ShowAllColumns$' &&
        !order &&
        qualificationsUpdate.length === 0;
      this.updateHiddenConfiguration(
        `${viewName}.columns`,
        configObject,
        isDefault,
      );
    },

    handleColumnWidthByUser(
      viewName,
      breakpoint,
      columnName1 = undefined,
      width1 = undefined,
      columnName2 = undefined,
      width2 = undefined,
    ) {
      if (
        !viewName ||
        !breakpoint ||
        !(columnName1 && width1) ||
        !(columnName2 && width2)
      ) {
        return;
      }

      const label = `${viewName}.columns.widths`;
      const existingConfig = this.configurations[label] || {};

      let updatedConfig = existingConfig;
      if (columnName1 && width1) {
        updatedConfig = {
          ...updatedConfig,
          [breakpoint]: {
            ...(updatedConfig[breakpoint] || {}),
            [columnName1]: width1,
          },
        };
      }
      if (columnName2 && width2) {
        updatedConfig = {
          ...updatedConfig,
          [breakpoint]: {
            ...(updatedConfig[breakpoint] || {}),
            [columnName2]: width2,
          },
        };
      }

      this.updateHiddenConfiguration(label, updatedConfig);
    },
    getColumnsWidthConfig(viewName, breakpoint) {
      if (!viewName || !breakpoint) {
        return {};
      }

      const label = `${viewName}.columns.widths`;
      const config = this.configurations[label];

      if (!config || typeof config !== 'object') {
        return {};
      }

      return config[breakpoint] || {};
    },

    getQualificationColumnsToDisplay(viewName) {
      const {qualifications} = this.getColumnsConfigs(viewName);
      const qualColumns = [];
      qualifications.forEach((qualId) => {
        const qualification = this.qualifications.find(
          (qual) => qual.id === qualId,
        );
        if (qualification) {
          qualColumns.push({
            id: qualId,
            designation: qualification.designation,
          });
        }
      });
      return qualColumns;
    },
  },
});
